import { Injectable } from '@angular/core';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import {
  AddPharmacyGroupDrugs,
  PharmacyGroupDrugDeleted,
  PharmacyGroupDrugUpdated,
  ThrowPharmacyGroupDrugError,
  FirePharmacyGroupDrugSuccess,
  PharmacyGroupDrugActionTypes,
  PharmacyGroupDrugsPageLoaded,
  PharmacyGroupDrugsPageRequested,
  PharmacyGroupDrugsPageCancelled,
  PharmacyGroupDrugOnServerUpdated,
  PharmacyGroupDrugOnServerDeleted,
  PharmacyGroupDrugsPageToggleLoading,
  PharmacyGroupDrugsActionToggleLoading,
} from '../_actions/pharmacy-group-drug.actions';
import { Router } from '@angular/router';
import { PharmacyGroupDrugService } from '../_services';
import { AuthService } from '../../auth/_services';

@Injectable()
export class PharmacyGroupDrugEffects {
  showPageLoadingDistpatcher = new PharmacyGroupDrugsPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new PharmacyGroupDrugsPageToggleLoading({ isLoading: false });
  showActionLoadingDistpatcher = new PharmacyGroupDrugsActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new PharmacyGroupDrugsActionToggleLoading({ isLoading: false });

  @Effect()
  loadPharmacyGroupDrugsPage$ = this.actions$.pipe(
    ofType<PharmacyGroupDrugsPageRequested>(
      PharmacyGroupDrugActionTypes.PharmacyGroupDrugsPageRequested,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.pharmacyGroupDrug.findPharmacyGroupDrugs(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin([requestToServer, lastQuery]).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0] && response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new PharmacyGroupDrugsPageCancelled();
            } else {
              this.store.dispatch(
                new ThrowPharmacyGroupDrugError({ error: response[0].error.error }),
              );
              return new PharmacyGroupDrugsPageCancelled();
            }
          } else {
            const result = response[0];
            return new PharmacyGroupDrugsPageLoaded({
              pharmacyGroupDrugs: result.data,
              totalCount: result.count,
              page: response[1],
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deletePharmacyGroupDrug$ = this.actions$.pipe(
    ofType<PharmacyGroupDrugOnServerDeleted>(
      PharmacyGroupDrugActionTypes.PharmacyGroupDrugOnServerDeleted,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.pharmacyGroupDrug
        .deletePharmacyGroupDrug(payload.id, payload.pharmacyGroupId)
        .pipe(
          catchError((error) => {
            return of({ error });
          }),
          map((res) => {
            if (res && res.error) {
              return new ThrowPharmacyGroupDrugError({ error: res.error });
            } else {
              this.store.dispatch(this.hideActionLoadingDistpatcher);
              this.store.dispatch(
                new FirePharmacyGroupDrugSuccess({
                  action_type: PharmacyGroupDrugActionTypes.PharmacyGroupDrugDeleted,
                }),
              );
              return new PharmacyGroupDrugDeleted(payload);
            }
          }),
        );
    }),
  );

  @Effect()
  updatePharmacyGroupDrug$ = this.actions$.pipe(
    ofType<PharmacyGroupDrugOnServerUpdated>(
      PharmacyGroupDrugActionTypes.PharmacyGroupDrugOnServerUpdated,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.pharmacyGroupDrug
        .updatePharmacyGroupDrug(payload.pharmacyGroupDrug, payload.pharmacyGroupId)
        .pipe(
          catchError((error) => {
            return of({ error });
          }),
          map((res) => {
            if (res && res.error) {
              return new ThrowPharmacyGroupDrugError({ error: res.error });
            } else {
              this.store.dispatch(this.hideActionLoadingDistpatcher);
              this.store.dispatch(
                new FirePharmacyGroupDrugSuccess({
                  action_type: PharmacyGroupDrugActionTypes.PharmacyGroupDrugUpdated,
                }),
              );
              return new PharmacyGroupDrugUpdated(payload);
            }
          }),
        );
    }),
  );

  @Effect()
  addPharmacyGroupDrugs$ = this.actions$.pipe(
    ofType<AddPharmacyGroupDrugs>(PharmacyGroupDrugActionTypes.AddPharmacyGroupDrugs),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.pharmacyGroupDrug
        .addPharmacyGroupDrugs(payload.drugs, payload.pharmacyGroupId)
        .pipe(
          catchError((error) => {
            return of(error);
          }),
          map((res) => {
            if (res && res.error) {
              return new ThrowPharmacyGroupDrugError({ error: res.error });
            } else {
              this.store.dispatch(this.hideActionLoadingDistpatcher);
              return new FirePharmacyGroupDrugSuccess({
                action_type: PharmacyGroupDrugActionTypes.AddPharmacyGroupDrugs,
              });
            }
          }),
        );
    }),
  );

  constructor(
    private actions$: Actions,
    private pharmacyGroupDrug: PharmacyGroupDrugService,
    private store: Store<AppState>,
    private router: Router,
    private authService: AuthService,
  ) {}
}
